import axios from "axios";
import { BASE_URL } from "./apiConfig";

const axiosInstance = axios.create({
  baseURL: BASE_URL,
});

axiosInstance.interceptors.request.use(
  (config) => {
    const isFormData =
      typeof FormData !== "undefined" && config.data instanceof FormData;
    if (isFormData) {
      // deixa o browser setar o boundary correto
      if (config.headers) {
        delete config.headers["Content-Type"];
        delete config.headers["content-type"];
      }
    } else if (config.data && config.headers) {
      // garante JSON quando há body não-FormData
      if (!config.headers["Content-Type"] && !config.headers["content-type"]) {
        config.headers["Content-Type"] = "application/json";
      }
    }
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// -----------------------------------------------------------------------------
// Dedup de GETs IDÊNTICOS EM ANDAMENTO (sem cache: só enquanto a 1ª resposta não
// volta). Várias partes da tela pedem o mesmo dado de referência ao mesmo tempo
// (usuário atual, lista de usuários, funis, tarefas/agenda de hoje…) — a cada
// carregamento eram 2 a 4 requisições iguais. Quem pega carona recebe uma CÓPIA
// dos dados, para um consumidor não alterar o resultado do outro. Como a
// resposta é descartada ao chegar, nada fica velho: o próximo GET vai ao servidor.
// -----------------------------------------------------------------------------
const DEDUPE_GET =
  /^\/(user-current|users|crm\/funnels|crm\/tasks\/today|calendar\/events|dashboard\/user\/overview)(\?|$)/;
const inflightGets = new Map();
const cloneData = (data) => {
  try {
    return typeof structuredClone === "function"
      ? structuredClone(data)
      : JSON.parse(JSON.stringify(data));
  } catch {
    return data;
  }
};
// Cache CURTO (15 s) só da lista completa de usuários da empresa: ~25 telas e
// modais pedem exatamente `/users?itemsPerPage=-1` ao abrir, um depois do outro.
// Qualquer escrita em usuários/papéis/filiais limpa o cache (antes e depois da
// resposta), e uma leitura que começou antes da escrita não é gravada depois dela.
const USERS_ALL_URL = "/users?itemsPerPage=-1";
const USERS_CACHE_TTL_MS = 15000;
const USERS_WRITE = /\/(users?|roles?|filiais?|minhas-empresas)(\/|$|\?)/i;
let usersCache = null; // { token, res, expiresAt }
let usersInflight = null; // { token, epoch, promise }
let usersEpoch = 0;
const dropUsersCache = () => {
  usersEpoch += 1;
  usersCache = null;
};
axiosInstance.interceptors.request.use((config) => {
  if (
    String(config.method || "get").toLowerCase() !== "get" &&
    USERS_WRITE.test(String(config.url || ""))
  ) {
    dropUsersCache();
  }
  return config;
});
axiosInstance.interceptors.response.use(
  (response) => {
    if (
      String(response.config?.method || "get").toLowerCase() !== "get" &&
      USERS_WRITE.test(String(response.config?.url || ""))
    ) {
      dropUsersCache();
    }
    return response;
  },
  (error) => Promise.reject(error),
);

const rawGet = axiosInstance.get.bind(axiosInstance);
axiosInstance.get = (url, config) => {
  if (url === USERS_ALL_URL && !(config && (config.params || config.signal))) {
    const token = localStorage.getItem("token") || "";
    if (
      usersCache &&
      usersCache.token === token &&
      usersCache.expiresAt > Date.now()
    ) {
      return Promise.resolve({
        ...usersCache.res,
        data: cloneData(usersCache.res.data),
      });
    }
    // Leitura já em andamento (mesma época/token): pega carona na resposta.
    if (
      usersInflight &&
      usersInflight.token === token &&
      usersInflight.epoch === usersEpoch
    ) {
      return usersInflight.promise.then((res) => ({
        ...res,
        data: cloneData(res.data),
      }));
    }
    const epochAtStart = usersEpoch;
    const promise = rawGet(url, config)
      .then((res) => {
        if (epochAtStart === usersEpoch) {
          usersCache = {
            token,
            res: { ...res, data: cloneData(res.data) },
            expiresAt: Date.now() + USERS_CACHE_TTL_MS,
          };
        }
        return res;
      })
      .finally(() => {
        if (usersInflight && usersInflight.promise === promise) {
          usersInflight = null;
        }
      });
    usersInflight = { token, epoch: epochAtStart, promise };
    return promise;
  }
  const plain =
    typeof url === "string" &&
    DEDUPE_GET.test(url) &&
    !(config && (config.signal || config.cancelToken || config.responseType));
  if (!plain) return rawGet(url, config);
  const key = `${localStorage.getItem("token") || ""}|${url}|${JSON.stringify(
    (config && config.params) || {},
  )}`;
  const pending = inflightGets.get(key);
  if (pending) {
    return pending.then((res) => ({ ...res, data: cloneData(res.data) }));
  }
  const promise = rawGet(url, config).finally(() => inflightGets.delete(key));
  inflightGets.set(key, promise);
  return promise;
};

export default axiosInstance;
